import { Children, useLayoutEffect, useRef, useState } from "react";
import { EyeOff, Maximize2 } from "lucide-react";
import { useEditMode } from "@/lib/editMode";

/**
 * SortableGrid — iPhone-style widget grid.
 *
 * Pointer-events based DnD (mouse + touch + pen). Cycle-resize supports FOUR
 * shapes: Small (1x1) → Wide (2x1) → Large (2x2) → Tall (1x2) → Small.
 *
 * Public API — unchanged:
 *   items          : ordered list of ids
 *   onReorder(next): drag-drop reorder callback
 *   onHide(id)     : X-chip callback (hide/remove widget)
 *   onResize(id,sz): resize-chip callback
 *   sizes          : Record<id, "1x1"|"2x1"|"2x2"|"1x2">
 *   editMode       : bool — when omitted, falls back to global useEditMode()
 *   surface        : optional key for the global edit-mode lookup
 */

const SIZE_CLASS = {
  "1x1": "col-span-1 row-span-1",
  "2x1": "col-span-2 row-span-1",
  "2x2": "col-span-2 row-span-2",
  "1x2": "col-span-1 row-span-2",
};

// How long a displaced widget takes to glide into the slot it was pushed into.
const SWAP_MS = 220;

// iPhone-Photos-style rotation of sizes.
function nextSize(cur) {
  return { "1x1": "2x1", "2x1": "2x2", "2x2": "1x2", "1x2": "1x1" }[cur] || "1x1";
}

export default function SortableGrid({
  items,
  onReorder,
  onHide,
  onResize,
  sizes = {},
  editMode,
  surface,
  className = "",
  // iter111.2 · Row height is fixed at 200 for Today (so 1×1/2×1/1×2/2×2
  // produce distinct aspect ratios). Other surfaces (e.g. PatientProgress
  // wellness page) pass `rowHeight="auto"` so a card can grow to fit its
  // chart + legend + source footnote instead of clipping past 200 px.
  rowHeight = "200px",
  children,
}) {
  // Fall back to the global per-tab edit-mode toggle when the caller doesn't
  // pass editMode explicitly — that's the whole point of the DashLayout
  // Customize Layout button.
  const global = useEditMode(surface);
  const isEditing = typeof editMode === "boolean" ? editMode : global.editing;

  const gridRef = useRef(null);
  const dragId = useRef(null);
  const [overId, setOverId] = useState(null);
  // Mirrors `dragId` for rendering — the handlers need the value synchronously
  // (ref), the tile styling needs a re-render (state).
  const [draggingId, setDraggingId] = useState(null);

  // ------ Pointer-based DnD ---------------------------------------------
  // Pointer events unify mouse + touch + pen. We start on pointerdown,
  // track the id under the pointer on pointermove (using elementFromPoint
  // so touch works — HTML5 DnD would have failed there), and commit on
  // pointerup. This is what makes the widget grid usable on iPhone.
  const findIdAt = (clientX, clientY) => {
    const el = document.elementFromPoint(clientX, clientY);
    if (!el || !gridRef.current || !gridRef.current.contains(el)) return null;
    const holder = el.closest("[data-sortable-id]");
    return holder ? holder.dataset.sortableId : null;
  };

  // Swap, not insert: the widget under the cursor takes the slot the dragged
  // widget came from. Always derived from `items` plus the current target, so
  // moving across several tiles can't compound into a surprise order.
  const swapped = (order, a, b) => {
    const next = [...order];
    const i = next.indexOf(a);
    const j = next.indexOf(b);
    if (i === -1 || j === -1) return order;
    next[i] = b;
    next[j] = a;
    return next;
  };

  // The element being carried, where inside it the pointer grabbed, and the
  // last pointer position. Written straight to `style` on pointermove: the
  // tile has to track the cursor at pointer rate, and re-rendering every card
  // in the grid once per frame would stutter.
  const dragEl = useRef(null);
  const grabOffset = useRef({ x: 0, y: 0 });
  const pointerAt = useRef({ x: 0, y: 0 });
  // Rects captured immediately before a preview swap, so whatever changed slot
  // can be animated from where it was to where it landed.
  const flipFrom = useRef(null);
  // The widget we last swapped with. Re-entering it must not swap again, or a
  // drag that lingers over a neighbour flips back and forth and can land back
  // where it started. Cleared when the pointer leaves every widget, so moving
  // on to another one still swaps immediately — no dead zone to reach, which
  // is what made side-by-side widgets feel unswappable.
  const lastTarget = useRef(null);
  const snapshotRects = () => {
    const map = new Map();
    gridRef.current?.querySelectorAll("[data-sortable-id]").forEach((el) => {
      map.set(el.dataset.sortableId, el.getBoundingClientRect());
    });
    return map;
  };

  // Glide each displaced widget into its new slot with the Web Animations API
  // rather than an inline transition. This element's `style` is owned by
  // React and every preview swap re-renders, so an inline transition is
  // patched away before it can paint — that is why the first attempt at this
  // never animated. A script animation also outranks the jiggle keyframes, so
  // the wobble does not have to be paused.
  const glideDisplaced = (before) => {
    if (!before) return;
    const carried = dragId.current;
    gridRef.current?.querySelectorAll("[data-sortable-id]").forEach((el) => {
      const id = el.dataset.sortableId;
      if (id === carried) return;
      const prev = before.get(id);
      if (!prev) return;
      const now = el.getBoundingClientRect();
      const dx = prev.left - now.left;
      const dy = prev.top - now.top;
      if (!dx && !dy) return;
      el.animate(
        [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: "translate(0px, 0px)" }],
        { duration: SWAP_MS, easing: "cubic-bezier(.2,.8,.2,1)" },
      );
    });
  };

  // Position the carried tile under the cursor. The tile keeps its place in
  // the grid, and the live swap preview moves its slot around, so the offset
  // is measured against the tile's current layout box rather than against a
  // fixed start point — otherwise it would jump every time the order changed.
  // Reads layout once per call; fine for a handful of tiles, worth caching if
  // a grid ever holds many.
  const placeTile = () => {
    const el = dragEl.current;
    if (!el) return;
    el.style.transform = "none";
    const box = el.getBoundingClientRect();
    const x = pointerAt.current.x - grabOffset.current.x - box.left;
    const y = pointerAt.current.y - grabOffset.current.y - box.top;
    el.style.transform = `translate(${x}px, ${y}px) scale(1.02)`;
    el.style.boxShadow = "0 18px 40px -12px rgba(0,0,0,0.65)";
  };

  const liftTile = (el) => {
    dragEl.current = el;
    // The jiggle keyframes animate `transform`, and a running animation wins
    // over an inline transform — so the jiggle has to stop for this tile
    // while it is being carried.
    el.style.animation = "none";
    el.style.transition = "none";
    el.style.zIndex = "40";
    // Without this the tile sits under the cursor and elementFromPoint would
    // only ever return the tile itself, so nothing could be dropped onto.
    el.style.pointerEvents = "none";
    el.style.willChange = "transform";
  };
  const dropTile = () => {
    const el = dragEl.current;
    dragEl.current = null;
    if (!el) return;
    for (const prop of ["animation", "transition", "zIndex", "pointerEvents", "willChange", "transform", "boxShadow"]) {
      el.style[prop] = "";
    }
  };

  // Runs before paint: re-anchor the carried tile (the preview swap changed
  // which cell it occupies) and glide whatever it displaced.
  useLayoutEffect(() => {
    const before = flipFrom.current;
    flipFrom.current = null;
    if (dragEl.current) placeTile();
    glideDisplaced(before);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overId]);

  const handlePointerDown = (id) => (e) => {
    if (!isEditing) return;
    // Skip when the user actually pressed the hide/resize chip
    if (e.target?.closest?.("[data-chip]")) return;
    const box = e.currentTarget.getBoundingClientRect();
    dragId.current = id;
    setDraggingId(id);
    grabOffset.current = { x: e.clientX - box.left, y: e.clientY - box.top };
    pointerAt.current = { x: e.clientX, y: e.clientY };
    lastTarget.current = null;
    liftTile(e.currentTarget);
    placeTile();
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const handlePointerMove = (e) => {
    if (!isEditing || !dragId.current) return;
    pointerAt.current = { x: e.clientX, y: e.clientY };
    placeTile();
    const overNext = findIdAt(e.clientX, e.clientY);
    if (!overNext) { lastTarget.current = null; return; }
    if (overNext === dragId.current || overNext === lastTarget.current) return;
    lastTarget.current = overNext;
    flipFrom.current = snapshotRects();
    setOverId(overNext);
  };
  const handlePointerUp = () => {
    if (!isEditing) return;
    const src = dragId.current;
    const tgt = overId;
    dragId.current = null;
    lastTarget.current = null;
    dropTile();
    setDraggingId(null);
    setOverId(null);
    if (!src || !tgt || src === tgt || !items?.length) return;
    const next = swapped(items, src, tgt);
    if (next !== items) onReorder?.(next);
  };

  return (
    <div
      ref={gridRef}
      className={`${className} ${isEditing ? "fl-jiggle" : ""}`}
      style={{
        gridAutoRows: rowHeight === "auto" ? "minmax(200px, auto)" : rowHeight,
        touchAction: isEditing ? "none" : "auto",
      }}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      {/* Render in `items` order, and drop ids `items` no longer contains
          (that is how a hidden widget disappears). Call sites may therefore
          list their <SortableGrid.Item>s in plain source order — dragging
          still reorders, because order comes from `items`, not from JSX.
          While a drag is in progress the order shown is the live swap
          preview, so the widget under the cursor visibly moves into the slot
          the carried one came from before the pointer is even released. */}
      {(() => {
        const kids = Children.toArray(children).filter((c) => c?.props?.id);
        if (!items?.length) return kids;
        const order = draggingId && overId && overId !== draggingId
          ? swapped(items, draggingId, overId)
          : items;
        return order.map((id) => kids.find((c) => c.props.id === id)).filter(Boolean);
      })().map((child) => {
        const id = child.props.id;
        const size = sizes[id] || "1x1";
        const carried = isEditing && draggingId === id;
        return (
          <div
            key={id}
            data-sortable-id={id}
            onPointerDown={handlePointerDown(id)}
            className={`${SIZE_CLASS[size]} relative`}
            style={{
              // The carried tile is the one that gets highlighted; the tile
              // underneath already announces itself by sliding into the
              // vacated slot, so it needs no outline of its own.
              transition: carried ? "none" : "outline-color 220ms",
              // Dashed, as it always was — only the colour marks the carried
              // tile, so the border style the customer is used to is kept.
              outline: carried ? "2px dashed var(--fl-orange)" : "2px dashed transparent",
              outlineOffset: 4,
              cursor: isEditing ? "grab" : "default",
              userSelect: isEditing ? "none" : "auto",
            }}
            data-testid={`sortable-item-${id}`}
          >
            {isEditing && onHide && (
              <button
                type="button"
                data-chip
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); onHide(id); }}
                aria-label={`Remove ${id}`}
                title="Hide this widget"
                className="absolute -top-2 -left-2 z-20 w-6 h-6 rounded-full flex items-center justify-center font-bold"
                style={{
                  background: "var(--fl-orange)",
                  color: "#0A0A0A",
                  boxShadow: "0 6px 14px -6px rgba(244,122,32,0.55)",
                }}
                data-testid={`sortable-${id}-hide`}
              >
                <EyeOff size={12} />
              </button>
            )}
            {isEditing && onResize && (
              <button
                type="button"
                data-chip
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); onResize(id, nextSize(size)); }}
                aria-label={`Resize ${id}`}
                title={`Size: ${size} — click to cycle`}
                className="absolute -bottom-2 -right-2 z-20 flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-semibold uppercase tracking-wider"
                style={{
                  background: "var(--fl-orange)",
                  color: "#0A0A0A",
                  boxShadow: "0 6px 14px -6px rgba(244,122,32,0.55)",
                }}
                data-testid={`sortable-${id}-resize`}
              >
                <Maximize2 size={10} /> {size}
              </button>
            )}
            {child}
          </div>
        );
      })}
    </div>
  );
}

SortableGrid.Item = function Item({ children }) {
  return <>{children}</>;
};
